import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { CompanySearchResult } from '../../../api/models/company-search-result';
import { InfoTooltipComponent } from '../info-tooltip.component';

/** One suggestion prepared for display: the company plus its two rendered lines. */
export interface CompanySuggestionRow {
  company: CompanySearchResult;
  /** Title line — the company name, with the matched former name behind it when that is why it hit. */
  name: string;
  /** Subtitle line — where the company sits and under which KvK number. */
  meta: string;
}

/**
 * Builds the subtitle of a suggestion: the address the search returned plus the KvK number.
 *
 * The search API answers a name query with a street and a city only (house number and postcode are
 * filled in for an address query), which is exactly enough to tell two locations of one company
 * apart — the full address is read from the company profile once a suggestion is picked.
 */
export function companySuggestionMeta(company: CompanySearchResult): string {
  const address =
    company.fullAddress ??
    [company.streetName, company.postalCode, company.city].filter(Boolean).join(', ');
  const number = company.chamberOfCommerceNumber ? `KVK ${company.chamberOfCommerceNumber}` : '';
  return [address, number].filter(Boolean).join(' · ');
}

/** Builds the title of a suggestion. */
export function companySuggestionName(company: CompanySearchResult): string {
  const name = company.name?.trim() || 'Onbekend bedrijf';
  // A company found under a name it no longer trades under is shown under its current name, with the
  // old one behind it — otherwise the user searching for the name they know sees no match at all.
  return company.expiredName ? `${name} (voorheen ${company.expiredName})` : name;
}

/**
 * The company search box: a text field that offers KvK suggestions to pick from.
 *
 * Presentational on purpose — it searches nothing itself. What was typed leaves as `queryChange`,
 * suggestions come back in as `suggestions`, and picking one leaves as `suggestionPicked`. The
 * surface around it decides what a query costs and what a pick means.
 *
 * Encapsulation is off because the option rows render in a CDK overlay, outside this component's
 * DOM, where scoped styles never reach them. Every selector is namespaced `dd-company-…` for that
 * reason.
 */
@Component({
  selector: 'app-company-search-field',
  template: `
    <mat-form-field class="w-full" [class.mat-form-field-invalid]="invalid()">
      <mat-label>
        {{ label() }}
        @if (info()) {
          <app-info-tooltip [tooltip]="info()"></app-info-tooltip>
        }
      </mat-label>
      <!-- The magnifier says the field is searched rather than filled in. It steps aside once a
           company is picked: the box then holds an answer, not a query. -->
      @if (!confirmed()) {
        <mat-icon
          matPrefix
          class="icon-size-5 mr-2 text-hint"
          svgIcon="heroicons_outline:magnifying-glass"></mat-icon>
      }
      <input
        #searchInput
        #autocompleteTrigger="matAutocompleteTrigger"
        matInput
        type="text"
        [id]="inputId()"
        [formControl]="searchControl"
        [matAutocomplete]="companyAuto"
        [required]="required()"
        [readonly]="readOnly()"
        [placeholder]="placeholder()"
        (focus)="onFocus()"
        (blur)="onBlur(autocompleteTrigger.panelOpen)"
        data-cy="company-search-input" />
      <span matSuffix class="dd-clear-suffix dd-addon-suffix">
        @if (loading()) {
          <mat-spinner class="mr-2" diameter="18" aria-label="Bedrijven zoeken"></mat-spinner>
        } @else if (confirmed()) {
          <mat-icon class="dd-addon-suffix__passive text-primary icon-size-5">check_circle</mat-icon>
        }
        <ng-content select="[companySearchSuffix]"></ng-content>
      </span>
      <!--
        Status and description share Material's subscript slot rather than sitting under the field
        as their own paragraph: that slot is the space the form field already reserves, so the line
        can never end up on top of the input's own border. The status wins when both apply — it says
        something about the search the user just ran, the description only repeats the standing
        instruction.
      -->
      @if (statusMessage(); as message) {
        <mat-hint [class.text-warn]="failed()" data-cy="company-search-status">{{ message }}</mat-hint>
      } @else if (description()) {
        <mat-hint>{{ description() }}</mat-hint>
      }
      <!--
        displayWith is what keeps the input readable: Material writes the selected option's VALUE
        into the native input before it emits optionSelected, and that value is the raw
        CompanySearchResult — without this the field flashes "[object Object]".
      -->
      <mat-autocomplete
        #companyAuto="matAutocomplete"
        class="dd-company-panel"
        [displayWith]="displaySuggestion"
        (optionSelected)="suggestionPicked.emit($event.option.value)"
        (closed)="onAutocompleteClosed()"
        autoActiveFirstOption>
        @for (row of rows(); track row.company.establishmentNumber ?? row.company.chamberOfCommerceNumber ?? $index) {
          <mat-option class="dd-company-option" [value]="row.company">
            <span class="dd-company-option__name">{{ row.name }}</span>
            <span class="dd-company-option__meta">{{ row.meta }}</span>
          </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
  `,
  imports: [
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatProgressSpinner,
    MatIcon,
    InfoTooltipComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      /* Two lines per suggestion: Material sizes an option for one, so the row has to grow and its
         text wrapper has to stack instead of centring a single line.

         Scoped under the panel class deliberately: styles/components/_mat-autocomplete.scss sets a
         36px min-height on .mat-mdc-option inside .mat-mdc-autocomplete-panel, which is the same
         specificity as .dd-company-option.mat-mdc-option on its own — a tie that only source order
         settles, and this component's styles are injected at runtime rather than bundled. The extra
         class makes the win explicit instead of incidental. */
      .mat-mdc-autocomplete-panel .dd-company-option.mat-mdc-option {
        height: auto;
        min-height: 56px;
        padding-top: 8px;
        padding-bottom: 8px;
      }

      .dd-company-option .mdc-list-item__primary-text {
        display: flex;
        flex-direction: column;
        gap: 2px;
        width: 100%;
        white-space: normal;
      }

      .dd-company-option__name {
        font-weight: 500;
        line-height: 1.3;
      }

      .dd-company-option__meta {
        font-size: 0.75rem;
        line-height: 1.3;
        opacity: 0.7;
      }
    `,
  ],
})
export class CompanySearchFieldComponent {
  readonly label = input('Bedrijf');
  readonly placeholder = input('Zoek op bedrijfsnaam of KVK-nummer');
  readonly description = input<string | null>(null);
  readonly info = input<string | null>(null);
  readonly inputId = input<string | null>(null);
  readonly required = input(false);
  readonly disabled = input(false);
  /**
   * Has zero production bindings since the read-mode sweep (#21239) removed the last one
   * (`kvk.component.ts`'s `<app-company-search-field>` call binds no `[readOnly]`). Kept because
   * it is this widget's own documented read-only affordance — a disabled `mat-form-field` with a
   * real `<input readonly>`, distinct from the Formly read-mode row — exercised by
   * `bedrijf-zoeken.stories.ts`'s `AlleenLezen` story; see that story's own docblock for why it
   * deliberately stays outside the #21239 sweep. Removing this input would break that story and
   * this component's own template at compile time.
   */
  readonly readOnly = input(false);
  readonly invalid = input(false);
  /** A lookup answered for the company in the box — drives the check mark. */
  readonly confirmed = input(false);
  readonly loading = input(false);
  readonly suggestions = input<readonly CompanySearchResult[]>([]);
  /** What the box shows. Set by the surface after a pick, so the field reads as the chosen company. */
  readonly value = input('');
  /** The search ran and matched nothing. */
  readonly notFound = input(false);
  /** The search could not be run at all. Said differently from "no such company": one means try
   *  again, the other means fill it in by hand. */
  readonly failed = input(false);
  /** Below this many characters the surface does not search, which the field says rather than hides. */
  readonly minQueryLength = input(3);

  /** What was typed, unthrottled — the surface decides what a query costs. */
  readonly queryChange = output<string>();
  readonly suggestionPicked = output<CompanySearchResult>();
  /** The search interaction ended without a suggestion being picked. */
  readonly searchBlur = output<void>();

  readonly searchControl = new FormControl('', { nonNullable: true });

  /**
   * What the box currently reads, typed or handed in. Tracked separately from the control because
   * the control also briefly holds the picked object during option selection, and because a value
   * set from outside is applied silently — a status message must still follow it.
   */
  readonly #boxText = signal('');
  #cleanupOnPanelClose = false;

  readonly rows = computed<CompanySuggestionRow[]>(() =>
    this.suggestions().map(company => ({
      company,
      name: companySuggestionName(company),
      meta: companySuggestionMeta(company),
    }))
  );

  readonly statusMessage = computed<string | null>(() => {
    const text = this.#boxText().trim();
    if (this.loading() || this.readOnly() || this.disabled() || this.confirmed() || text.length === 0) {
      return null;
    }
    if (text.length < this.minQueryLength()) {
      return `Typ minstens ${this.minQueryLength()} tekens.`;
    }
    // A failed search is not an answer about the company: telling the user it does not exist would
    // send them off to type its details by hand over a backend that was briefly down.
    if (this.failed()) {
      return 'Zoeken mislukt. Probeer het opnieuw of vul de gegevens handmatig in.';
    }
    return this.notFound() ? 'Geen bedrijf gevonden.' : null;
  });

  constructor() {
    // The box is told what to show rather than owning it: a pick, a reset or a value loaded from the
    // entry all arrive this way. Silent, because echoing it back as a query would search for what was
    // just answered.
    effect(() => {
      const value = this.value();
      if (this.searchControl.value !== value) {
        this.searchControl.setValue(value, { emitEvent: false });
      }
      this.#boxText.set(value);
    });

    effect(() => {
      const disabled = this.disabled();
      if (disabled === this.searchControl.disabled) {
        return;
      }
      // emitEvent false: enabling/disabling is not the user typing.
      if (disabled) {
        this.searchControl.disable({ emitEvent: false });
      } else {
        this.searchControl.enable({ emitEvent: false });
      }
    });

    this.searchControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => {
      // Only a typed string is a query; the object Material writes during selection is not.
      if (typeof value === 'string') {
        this.#boxText.set(value);
        this.queryChange.emit(value);
      }
    });
  }

  /**
   * Leaving the box without picking anything puts back what the field actually holds.
   *
   * A typed query that matched nothing is a question, not an answer — leaving it on screen makes
   * the field read as one company while the surface still holds another, with nothing to say which
   * of the two gets saved. When options are open, cleanup waits for Material's `closed` event so a
   * pointer selection can finish first. A selection focuses the input again and cancels that
   * pending cleanup; the surface already clears the search when it handles `suggestionPicked`.
   */
  onBlur(panelOpen: boolean): void {
    const value = this.value();
    if (this.searchControl.value !== value) {
      this.searchControl.setValue(value, { emitEvent: false });
    }
    this.#boxText.set(value);
    if (panelOpen) {
      this.#cleanupOnPanelClose = true;
    } else {
      this.searchBlur.emit();
    }
  }

  onFocus(): void {
    this.#cleanupOnPanelClose = false;
  }

  onAutocompleteClosed(): void {
    if (!this.#cleanupOnPanelClose) {
      return;
    }
    this.#cleanupOnPanelClose = false;
    this.searchBlur.emit();
  }

  /**
   * Renders whatever Material puts in the control back to a string. Declared as an arrow property so
   * the template can hand the reference to [displayWith] without losing `this`.
   */
  readonly displaySuggestion = (value: CompanySearchResult | string | null): string => {
    if (typeof value === 'string') {
      return value;
    }
    return value ? companySuggestionName(value) : '';
  };
}
